/*
  Blair TLV product lightbox (Claude 2026-07-09).
  <product-modal> extends Dawn's ModalDialog (focus trap, body-scroll lock,
  Escape-to-close, pauseAllMedia) and adds an Apple-Music-style ambient preview:
    - a horizontal transform track of 1:1 square slides
    - swipe (touch) / drag (mouse) / arrow keys move between items
    - pinch (2-finger), ctrl+wheel, or double-tap zooms the active media;
      while zoomed, dragging pans and slide-swiping is disabled
    - the active slide's video autoplays (muted/loop/playsinline); others pause
  No external libraries: plain Pointer Events + a hand-rolled gesture handler.
*/

if (!customElements.get('product-modal')) {
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const SWIPE_RATIO = 0.16; // fraction of slide width needed to change slide
  const DOUBLE_TAP_MS = 300;

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Blair 2026-10-20: with data saver or reduced motion a video slide stays a still until tapped.
        this.holdVideo = this.reducedMotion || !!(navigator.connection && navigator.connection.saveData);
        this.index = 0;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.pointers = new Map();
        this.dragMode = null; // 'swipe' | 'pan' | 'pinch' | null
        this._lastTap = 0;
        this._bound = false;
      }

      _cache() {
        if (this._cached) return;
        this.track = this.querySelector('[data-bt-track]');
        this.stage = this.querySelector('.bt-lightbox__stage');
        this.counter = this.querySelector('[data-bt-counter]');
        this.prevBtn = this.querySelector('[data-bt-prev]');
        this.nextBtn = this.querySelector('[data-bt-next]');
        this._cached = !!this.track;
      }

      _bindOnce() {
        if (this._bound || !this.track) return;
        this._bound = true;

        const t = this.track;
        t.addEventListener('pointerdown', this._onPointerDown.bind(this));
        t.addEventListener('pointermove', this._onPointerMove.bind(this));
        t.addEventListener('pointerup', this._onPointerUp.bind(this));
        t.addEventListener('pointercancel', this._onPointerUp.bind(this));
        // ctrl+wheel to zoom on desktop (never let the page scroll under us)
        t.addEventListener('wheel', this._onWheel.bind(this), { passive: false });

        this.addEventListener('keydown', this._onKeyDown.bind(this));

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.go(this.rtl() ? this.index + 1 : this.index - 1));
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.go(this.rtl() ? this.index - 1 : this.index + 1));
        }
      }

      rtl() {
        return (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
      }

      /* ---- open / close ---------------------------------------------------- */

      /*
        Blair 2026-10-19 (phone QA: the viewer had more photos than the gallery on 24 products). The viewer
        renders every media of the product; the GALLERY decides what the shopper sees (other variants'
        photos hidden, only the current measurement image, Dawn's variant re-render). So the viewer mirrors
        the gallery's visible slides, in the gallery's order, on load, whenever the gallery changes, and on
        every open. Falls back to the viewer's own [hidden] state when there is no gallery to follow.
      */
      syncWithGallery() {
        const sec = (this.id || '').replace('ProductModal-', '');
        const list = sec && document.querySelector(`#MediaGallery-${sec} .product__media-list`);
        const track = this.querySelector('[data-bt-track]');
        if (!list || !track) return false;
        const ids = Array.from(list.children)
          .filter((li) => li.matches('li[data-media-id]') && !li.hidden)
          .map((li) => (li.dataset.mediaId || '').split('-').pop());
        if (!ids.length) return false;
        const slides = Array.from(track.querySelectorAll('.bt-lightbox__slide'));
        const byId = new Map(slides.map((s) => [s.getAttribute('data-media-id'), s]));
        slides.forEach((s) => { s.hidden = true; });
        ids.forEach((id) => {
          const s = byId.get(id);
          if (s) { s.hidden = false; track.appendChild(s); }
        });
        return true;
      }

      watchGallery() {
        if (this._galleryWatch) return;
        const sec = (this.id || '').replace('ProductModal-', '');
        const gallery = sec && document.getElementById(`MediaGallery-${sec}`);
        if (!gallery) return;
        let raf = 0;
        this._galleryWatch = new MutationObserver(() => {
          if (raf) return;
          raf = requestAnimationFrame(() => { raf = 0; if (!this.hasAttribute('open')) this.syncWithGallery(); });
        });
        this._galleryWatch.observe(gallery, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
        this.syncWithGallery();
      }

      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();
        const go = () => this.watchGallery();
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go, { once: true });
        else go();
      }

      show(opener) {
        this._cache();
        this.syncWithGallery();
        // Blair 2026-10-18: re-read on every open. Measurement slides of the other sizes/languages are
        // [hidden] (the gallery's variant swap toggles them), so they are not part of the viewer.
        this.slides = Array.from(this.querySelectorAll('.bt-lightbox__slide')).filter((s) => !s.hidden);
        this._bindOnce();
        super.show(opener); // focus trap + body lock + pauseAllMedia

        let target = 0;
        const id = opener && opener.getAttribute && opener.getAttribute('data-media-id');
        if (id) {
          const i = this.slides.findIndex((s) => s.getAttribute('data-media-id') === id);
          if (i >= 0) target = i;
        }
        this.index = target;
        this._resetZoom(false);
        this._layout(false);
        this._activate();
      }

      hide() {
        this._resetZoom(false);
        if (this.slides) {
          this.slides.forEach((s) => {
            const v = s.querySelector('video');
            if (v) v.pause();
          });
        }
        /*
          Founder 2026-07-30: "fix the bug when i exit preview the card scrolls up, maybe
          its not 1:1 scale". It is not a scale problem. Dawn's ModalDialog returns focus to
          the opener on close (removeTrapFocus -> element.focus()), and focusing an element
          that is off-screen makes the browser scroll it into view. Measured on the dev
          server: open the lightbox at scrollY 1200, close it, land at 305 — the page jumps
          up by 895px to put the gallery toggle back in view. Pin the page where it was.
        */
        const y = window.scrollY || window.pageYOffset || 0;
        super.hide(); // removes body lock, pauseAllMedia, releases focus trap
        const restore = () => window.scrollTo(0, y);
        restore();
        requestAnimationFrame(restore);
        setTimeout(restore, 0);
      }

      /* ---- slide navigation ------------------------------------------------ */

      go(i) {
        if (this.scale > 1.02) return; // zoomed: swiping disabled
        const n = this.slides.length;
        if (!n) return;
        // Founder 2026-07-13: loop the carousel so BOTH arrows stay visible + usable on
        // every slide (prev on the first image wraps to the last, and vice versa) instead
        // of one button disappearing at the ends. Wrap jumps snap instantly (no whoosh
        // across the whole track); adjacent moves keep the slide animation.
        const wrapped = ((i % n) + n) % n;
        const isWrap = n > 1 && Math.abs(wrapped - this.index) > 1;
        this.index = wrapped;
        this._layout(!this.reducedMotion && !isWrap);
        this._activate();
      }

      _layout(animate) {
        if (!this.track) return;
        this.track.classList.toggle('is-dragging', !animate);
        // track is forced dir:ltr, so a positive index always shifts left
        const offset = -this.index * this.track.clientWidth;
        this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
        if (this.counter) {
          this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        }
        // Both arrows stay visible whenever there's more than one slide (the carousel loops);
        // with a single image there's nowhere to go, so hide them.
        const single = this.slides.length <= 1;
        if (this.prevBtn) this.prevBtn.toggleAttribute('hidden', single);
        if (this.nextBtn) this.nextBtn.toggleAttribute('hidden', single);
      }

      _activate() {
        this.slides.forEach((slide, i) => {
          const active = i === this.index;
          slide.classList.toggle('is-active', active);
          const v = slide.querySelector('video');
          if (!v) return;
          if (active) {
            // Blair 2026-10-20: the viewer's video is preload="none" (it used to download with the page);
            // showing its slide is what starts it. Held (data saver / reduced motion): wait for a tap.
            const held = this.holdVideo && !v.dataset.btStarted;
            slide.classList.toggle('is-held', held);
            if (!held) this._playVideo(v);
          } else {
            v.pause();
          }
        });
      }

      _playVideo(v) {
        v.dataset.btStarted = '1';
        v.muted = true;
        if (v.preload !== 'auto') v.preload = 'auto';
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }

      /* ---- zoom + pan ------------------------------------------------------ */

      _activeZoomEl() {
        const slide = this.slides[this.index];
        return slide ? slide.querySelector('[data-bt-zoom]') : null;
      }

      _applyZoom(animate) {
        const el = this._activeZoomEl();
        if (!el) return;
        el.classList.toggle('is-animating', !!animate && !this.reducedMotion);
        el.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
        if (this.track) this.track.classList.toggle('is-zoomed', this.scale > 1.02);
        // Founder 2026-07-12: grow the whole 1:1 box as you zoom in (not just the image inside
        // a fixed window). Subtle factor so it enlarges the frame without a second full zoom.
        if (this.stage) {
          const grow = this.scale > 1.02 ? Math.min(1 + (this.scale - 1) * 0.28, 1.5) : 1;
          this.stage.style.setProperty('--bt-stage-zoom', grow);
        }
      }

      _clampPan() {
        const el = this._activeZoomEl();
        if (!el) return;
        const rect = el.getBoundingClientRect();
        // rect is already scaled; max travel keeps the scaled media covering the stage
        const maxX = Math.max(0, (rect.width - rect.width / this.scale) / 2);
        const maxY = Math.max(0, (rect.height - rect.height / this.scale) / 2);
        this.tx = clamp(this.tx, -maxX, maxX);
        this.ty = clamp(this.ty, -maxY, maxY);
      }

      _resetZoom(animate) {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this._applyZoom(animate);
      }

      _zoomTo(scale, animate) {
        this.scale = clamp(scale, MIN_SCALE, MAX_SCALE);
        if (this.scale <= 1.02) {
          this.tx = 0;
          this.ty = 0;
        } else {
          this._clampPan();
        }
        this._applyZoom(animate);
      }

      /* ---- pointer gestures ------------------------------------------------ */

      _onPointerDown(e) {
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        try {
          this.track.setPointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }

        if (this.pointers.size === 2) {
          // begin pinch
          const pts = Array.from(this.pointers.values());
          this.dragMode = 'pinch';
          this._pinchStartDist = dist(pts[0], pts[1]) || 1;
          this._pinchStartScale = this.scale;
          this._pinchStartMid = mid(pts[0], pts[1]);
          this._panStartTx = this.tx;
          this._panStartTy = this.ty;
          return;
        }

        // single pointer
        this._startX = e.clientX;
        this._startY = e.clientY;
        this._trackBase = -this.index * this.track.clientWidth;
        this._panStartTx = this.tx;
        this._panStartTy = this.ty;
        this._moved = false;
        this.dragMode = this.scale > 1.02 ? 'pan' : 'swipe';
        if (this.dragMode === 'swipe') this.track.classList.add('is-dragging');
      }

      _onPointerMove(e) {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.dragMode === 'pinch' && this.pointers.size >= 2) {
          e.preventDefault();
          const pts = Array.from(this.pointers.values());
          const d = dist(pts[0], pts[1]) || 1;
          const m = mid(pts[0], pts[1]);
          this.scale = clamp((this._pinchStartScale * d) / this._pinchStartDist, MIN_SCALE, MAX_SCALE);
          // follow the fingers so the pinch feels anchored
          this.tx = this._panStartTx + (m.x - this._pinchStartMid.x);
          this.ty = this._panStartTy + (m.y - this._pinchStartMid.y);
          this._clampPan();
          this._applyZoom(false);
          return;
        }

        const dx = e.clientX - this._startX;
        const dy = e.clientY - this._startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) this._moved = true;

        if (this.dragMode === 'pan') {
          e.preventDefault();
          this.tx = this._panStartTx + dx;
          this.ty = this._panStartTy + dy;
          this._clampPan();
          this._applyZoom(false);
        } else if (this.dragMode === 'swipe') {
          e.preventDefault();
          this.track.style.transform = `translate3d(${this._trackBase + dx}px, 0, 0)`;
        }
      }

      _onPointerUp(e) {
        const wasPinch = this.dragMode === 'pinch';
        this.pointers.delete(e.pointerId);
        try {
          this.track.releasePointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }

        if (wasPinch) {
          if (this.pointers.size < 2) {
            this.dragMode = null;
            if (this.scale <= 1.02) this._resetZoom(true);
            else this._applyZoom(false);
          }
          return;
        }

        if (this.dragMode === 'swipe') {
          const dx = e.clientX - this._startX;
          const threshold = this.track.clientWidth * SWIPE_RATIO;
          this.track.classList.remove('is-dragging');
          if (dx <= -threshold) this.go(this.index + 1);
          else if (dx >= threshold) this.go(this.index - 1);
          else this._layout(!this.reducedMotion);
        } else if (this.dragMode === 'pan') {
          this._clampPan();
          this._applyZoom(false);
        }

        // double-tap / double-click to toggle zoom (only a clean tap, not a drag)
        if (!this._moved && this.pointers.size === 0) {
          const now = Date.now();
          if (now - this._lastTap < DOUBLE_TAP_MS) {
            this._lastTap = 0;
            if (this.scale > 1.02) this._zoomTo(1, true);
            else this._zoomTo(2.5, true);
          } else {
            this._lastTap = now;
            /*
              A clean tap anywhere outside the media closes.

              Founder 2026-07-30: "let the user close the preview by pressing the blur
              overlay not just the x button". This used to test for .bt-lightbox__bg, which
              could never match: that element is `pointer-events: none` (it is the blurred
              background image), so the tap always landed on .bt-lightbox__slide and nothing
              closed. While zoomed in a tap is part of panning, so it must not close.

              Test the COORDINATES against the stage box, not the event target: the media
              inside the stage is also `pointer-events: none`, so target-based tests report
              a tap on the photo itself as a tap on the backdrop and close the lightbox the
              user was trying to look at (measured).
            */
            const stage = this.slides[this.index] && this.slides[this.index].querySelector('.bt-lightbox__stage');
            const box = stage && stage.getBoundingClientRect();
            const onMedia =
              box &&
              e.clientX >= box.left &&
              e.clientX <= box.right &&
              e.clientY >= box.top &&
              e.clientY <= box.bottom;
            if (!onMedia && this.scale <= 1.02) {
              this.hide();
            } else if (onMedia) {
              // held video (data saver / reduced motion): a tap on it plays it
              const slide = this.slides[this.index];
              const v = slide && slide.querySelector('video');
              if (v && !v.dataset.btStarted) {
                slide.classList.remove('is-held');
                this._playVideo(v);
              }
            }
          }
        }

        this.dragMode = null;
      }

      _onWheel(e) {
        if (!e.ctrlKey) return; // trackpad pinch / ctrl+wheel only
        e.preventDefault();
        const factor = Math.exp(-e.deltaY * 0.0016);
        this._zoomTo(this.scale * factor, false);
      }

      _onKeyDown(e) {
        const key = e.key;
        if (key === 'ArrowRight') {
          e.preventDefault();
          this.go(this.rtl() ? this.index - 1 : this.index + 1);
        } else if (key === 'ArrowLeft') {
          e.preventDefault();
          this.go(this.rtl() ? this.index + 1 : this.index - 1);
        }
      }
    }
  );

  /*
    Open the lightbox when a gallery VIDEO is tapped. The autoplaying inline
    video sits above Dawn's modal-opener toggle (z-index), so a tap lands on the
    <video>, not the hidden toggle button. This delegate finds that container's
    opener and shows the same modal at the right slide. Image thumbnails keep
    using Dawn's own modal-opener toggle.
  */
  document.addEventListener(
    'click',
    (e) => {
      const container =
        e.target &&
        e.target.closest &&
        e.target.closest('.product__media-list .product-media-container.media-type-video');
      if (!container) return;
      const opener = container.querySelector('modal-opener[data-modal]');
      const toggle = container.querySelector('.product__media-toggle');
      if (!opener || !toggle) return;
      const modal = document.querySelector(opener.getAttribute('data-modal'));
      if (modal && typeof modal.show === 'function') {
        e.preventDefault();
        modal.show(toggle);
      }
    },
    true
  );
}
